import React, { ChangeEvent, useEffect } from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { ConfirmationModal, FormRows, ModalActionTypes, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { FormikProps, withFormik } from 'formik'
import { compose, fromRenderProps, withHandlers, withProps, withState } from 'recompose'

import Button from 'components/Button'
import { CTAContainer } from 'components/CTAContainer'
import {
  FormRow,
  StyledFormContainer,
  StyledTextInput,
} from 'components/FormContainer/styled-components'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { useFundingSkipLinkText } from 'pages/Institutions/utils'
import { TestIDs } from 'test/utils/testIds'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getVisitedPages } from 'utils/FlowProvider/utils'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withOptimizely } from 'utils/Optimizely'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'

import formRows, { ACHProps, handleSubmit, mapPropsToValues, validate } from './form'
import linkACHAccount from './mutation.gql'
import {
  FooterContainer,
  FormItem,
  MobileOnlyFootnote,
  StyledHeroDescription,
  StyledHeroHeader,
  StyledNote,
} from './styled-components'

type InnerProps = {
  isValid: boolean
  rows: FormRows
  modalBody: string
  modalConfirmSkip: string
  modalReturn: string
  modalTitle: string
  modalIsOpen?: boolean
  setModalIsOpen?: (value: boolean) => void
  handleSkipConfirm?: () => void
  setFieldValue?: (field: string, value: any, shouldRevalidate: boolean) => void
  handleSkip: () => void
  onRequestClose: () => void
  onChange: (e: React.SyntheticEvent) => void
  verificationText: string
  navLinkText: string
  submitCtaText: string
} & FormikProps<ACHProps> &
  WithFlowRouterProps &
  HeroTextProps &
  FunnelContextProps

// Create a trackable Add Funding Source button
const AddFundingSourceButton = withClickTrack<any, any>(
  TRACKER_IDS.MANUAL_FUNDING_SOURCE.ADD_FUNDING_SOURCE_BUTTON,
)(Button)

const DEFAULT_COPY = {
  modalBody:
    'In order for you to start saving and investing, Acorns securely connects to your bank to enable Round-Ups® and fund your investments. Your security is our primary concern, so we never store your bank login information.',
  modalConfirmSkip: 'Skip',
  modalReturn: 'Go back',
  modalTitle: 'Are you sure?',
  verificationText:
    "To verify that you own your checking account, we’ll make 2 small deposits into it, which you'll need to verify.",
  navLinkText: 'Skip',
  submitCtaText: 'Link bank',
}

export const enhance = compose<InnerProps, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, hasVisited, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      hasVisited,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withOptimizely,
  withPartnerProps,
  withFlowRouter,
  withRouter,
  withHeroText,
  withApolloErrorModal,
  withRemoteStrings,
  withState('modalIsOpen', 'setModalIsOpen', false),
  withProps(({ getRemoteString, heroText, partner }) => {
    heroText.header = getRemoteString('manual_funding_source_hero_header', heroText.header)
    heroText.text = getRemoteString('manual_funding_source_hero_text', heroText.text)

    return {
      rows: formRows(partner),
      heroText,
      modalBody: getRemoteString(
        'manual_funding_source_confirmation_modal_body',
        DEFAULT_COPY.modalBody,
      ),
      modalConfirmSkip: getRemoteString(
        'manual_funding_source_confirmation_modal_confirm_skip',
        DEFAULT_COPY.modalConfirmSkip,
      ),
      modalReturn: getRemoteString(
        'manual_funding_source_confirmation_modal_return',
        DEFAULT_COPY.modalReturn,
      ),
      modalTitle: getRemoteString(
        'manual_funding_source_confirmation_modal_header',
        DEFAULT_COPY.modalTitle,
      ),
      verificationText: getRemoteString(
        'manual_funding_source_verification_text',
        DEFAULT_COPY.verificationText,
      ),
      navLinkText: getRemoteString('manual_funding_source_nav_link_cta', DEFAULT_COPY.navLinkText),
      submitCtaText: getRemoteString(
        'manual_funding_source_link_bank_cta',
        DEFAULT_COPY.submitCtaText,
      ),
    }
  }),
  graphql(linkACHAccount, { name: 'linkACHAccount' }),
  withFormik({
    enableReinitialize: true,
    mapPropsToValues,
    handleSubmit,
    isInitialValid: true,
    validate,
    validateOnChange: false,
  }),
  withHandlers({
    handleSkip: ({ setModalIsOpen, flow, navLinkText }) => () => {
      setModalIsOpen(true)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.MANUAL_FUNDING_SOURCE.SKIP_LINK_BANK_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          cta_title: flow !== 'spend' ? navLinkText.props.source : null,
        },
      })
    },
    onRequestClose: ({ setModalIsOpen, modalReturn }) => () => {
      setModalIsOpen(false)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.MANUAL_FUNDING_SOURCE.SKIP_LINK_BANK_MODAL_CANCEL),
          ...getDefaultEventData('Button Tapped'),
          cta_title: modalReturn.props.source,
        },
      })
    },
    handleSkipConfirm: ({
      flow,
      partner,
      route,
      trackOptimizely,
      modalConfirmSkip,
      isDynamicFunnelEnabled,
      hasVisited,
      routeToPage,
    }) => () => {
      trackOptimizely('skip_manual_linking.cta_clicked')
      trackEvent('ACH:Skip', { location, partner, flow })
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.MANUAL_FUNDING_SOURCE.SKIP_LINK_BANK_MODAL_CONFIRM),
          ...getDefaultEventData('Button Tapped'),
          cta_title: modalConfirmSkip.props.source,
        },
        flow,
        partner,
      })

      if (isDynamicFunnelEnabled) {
        if (
          hasVisited(PageName.SecondChanceBankLink) ||
          hasVisited(PageName.RecurringInvestment) ||
          hasVisited(PageName.OneTimeInvestment)
        ) {
          routeToPage(PageName.ManualFundingSource, ActionType.Skip)
        } else {
          routeToPage(PageName.ManualFundingSource, ActionType.Switch)
        }
      } else {
        route({ from: FlowRoute.ManualFundingSource, action: 'skip' })
      }
    },
    onChange: props => async (e: React.SyntheticEvent) => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      await props.handleChange(e)

      if (props.submitCount > 0) {
        props.validateForm()
      }
    },
  }),
)

export const ManualFundingSource: React.FC<InnerProps> = props => {
  const { getRemoteString } = useRemoteStrings()
  const skipLinkText = useFundingSkipLinkText(props.flow)
  const shouldShowSkipButton = props.isDynamicFunnelEnabled
    ? !props.hasVisited(PageName.SecondChanceBankLink)
    : !getVisitedPages().includes(FlowRoute.SecondChanceBankLink)

  const handleRoutingMask = (e: ChangeEvent<HTMLInputElement>) => {
    const element = e
    const x = element.target.value.replace(/\D/g, '').match(/(\d{0,9})/)
    element.target.value = x[1]
    return props.onChange(element)
  }

  const onChange = (rowName: string) => {
    return rowName === 'routingNumber' ? handleRoutingMask : props.onChange
  }
  const noPaste = (rowName: string) => rowName === 'confirmAccountNumber'

  const shouldGrayOutCTA = shouldGrayOutCTACall({ values: props.values, validation: validate })

  useEffect(() => {
    if (props.modalIsOpen === true) {
      trackEvent('Container Viewed', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.MANUAL_FUNDING_SOURCE.SKIP_LINK_BANK_MODAL),
          ...getDefaultEventData('Container Viewed'),
        },
      })
    }
  }, [props.modalIsOpen])

  return (
    <>
      <Nav
        back={
          props.isDynamicFunnelEnabled ? props.isBackEnabled(PageName.ManualFundingSource) : true
        }
        backClickTracker={TRACKER_IDS.MANUAL_FUNDING_SOURCE.BACK_BUTTON}
        linkText={shouldShowSkipButton ? skipLinkText : undefined}
        onLink={shouldShowSkipButton ? props.handleSkip : undefined}
      />
      <Layout
        header={props.heroText.header}
        text={props.heroText.text}
        footnoteLock={props.heroText.footnoteLock}
        footnote={props.heroText.footnote}
      >
        <StyledHeroHeader>{props.heroText.header}</StyledHeroHeader>
        <StyledHeroDescription>{props.heroText.text}</StyledHeroDescription>
        <StyledFormContainer>
          <FormRow>
            {props.rows.map(innerRows =>
              innerRows.map(row => (
                <FormItem key={row.id}>
                  <StyledTextInput
                    testId={row.id}
                    className="text-input"
                    label={{
                      value: row.label as string,
                    }}
                    inputProps={{
                      name: row.name,
                      value: props.values[row.name],
                      onChange: onChange(row.name),
                      id: row.id,
                      placeholder: '000000000',
                      disabled: row.disabled,
                      autocomplete: 'off',
                      noPaste: noPaste(row.name),
                      inputMode: 'numeric',
                    }}
                    error={!!props.errors[row.name]}
                    errorText={props.errors[row.name] as string}
                  />
                </FormItem>
              )),
            )}
          </FormRow>
          <StyledNote>{props.verificationText}</StyledNote>
        </StyledFormContainer>
        <Separator />
        <CTAContainer>
          <MobileOnlyFootnote>
            {getRemoteString({ key: 'registration.bank_link_interstitial.footer' })}
          </MobileOnlyFootnote>
          <AddFundingSourceButton
            data-testid={TestIDs.MANUAL_FUNDING_SOURCE.SUBMIT_BUTTON}
            styleType={!props.isValid || shouldGrayOutCTA ? 'invalid' : undefined}
            onPress={props.submitForm}
            disabled={props.isSubmitting}
            buttonType={'submit'}
          >
            {props.isSubmitting ? (
              <Spinner height={20} width={20} white={true} />
            ) : (
              props.submitCtaText
            )}
          </AddFundingSourceButton>
        </CTAContainer>
        <FooterContainer>
          {getRemoteString({ key: 'registration.bank_link_interstitial.footer' })}
        </FooterContainer>
        <ConfirmationModal
          actions={[
            {
              label: props.modalConfirmSkip,
              type: ModalActionTypes.secondary,
              handler: props.handleSkipConfirm,
            },
            {
              label: props.modalReturn,
              type: ModalActionTypes.primary,
              handler: props.onRequestClose,
            },
          ]}
          onRequestClose={props.onRequestClose}
          isOpen={props.modalIsOpen}
          message={props.modalBody}
          title={props.modalTitle}
        />
      </Layout>
    </>
  )
}

export default enhance(ManualFundingSource)
